/**
 * `/api/skill` — how the models have actually been doing at one point.
 *
 * The meteogram asks how the models disagree about the next two days; this
 * asks the harder one, against what the nearest station actually measured.
 * Everything a cell *is* — its units, its decimals, its bucket label — comes
 * back with the answer, from the same registry that coloured the plates, so
 * nothing here restates any of it. The frontend's job is to lay it out and to
 * refuse to print a number the server called `null`.
 */

export interface SkillStation {
  id: string
  name: string
  /** How far the station is from the point asked about. Routinely forty miles
   *  in open country, which is why it is printed rather than implied. */
  distance_mi: number
  timezone: string
}

export interface SkillBucket {
  id: string
  label: string
}

export interface SkillField {
  id: string
  label: string
  units: string
  decimals: number
}

export interface SkillScore {
  model: string
  field: string
  bucket: string
  /** `forecast - observed`: positive means the model forecast *higher*.
   *  `null` below `min_pairs`, where a number would be read as a measurement. */
  bias: number | null
  mae: number | null
  n: number
}

export interface SkillModel {
  id: string
  label: string
  runs: number
  pairs: number
  /** Present only when this model has nothing in the archive for this window.
   *  A sentence, never an absence: a model nobody ingested looks identical. */
  reason?: string
}

export interface SkillUnscored {
  field: string
  label: string
  reason: string
}

export interface SkillWindow {
  from: string
  to: string
  hours: number
  observations: number
  /** How much of the requested window the archive actually spans. Short of
   *  `hours` on a deployment that has not been running long — which reads as
   *  `collecting`, because a feature warming up and a feature broken must not
   *  look the same. */
  coverage_hours: number
}

export interface SkillBody {
  station: SkillStation | null
  window: SkillWindow
  buckets: SkillBucket[]
  fields: SkillField[]
  scores: SkillScore[]
  unscored: SkillUnscored[]
  models: SkillModel[]
  observed_errors: { field: string; reason: string }[]
  min_pairs: number
  generated: string
}

/**
 * The windows the panel offers.
 *
 * Capped at seven days, and the ceiling is **NWS's rather than the archive's**:
 * `/observations` caps `limit` at 500 and answers a 400 above it, and a busy
 * airport reports every five minutes — a day at KDEN is 316 features and
 * 1.3 MB. So a window costs one upstream request per UTC day, which is fine at
 * seven and is forty megabytes against a public NOAA service at thirty. The
 * archive keeps ninety days regardless; retention is about not losing the
 * option.
 *
 * 24 h stays the default, because "how have they been doing lately" is the
 * question people actually arrive with.
 */
export const SKILL_WINDOWS = [
  { hours: 24, label: '24 h' },
  { hours: 72, label: '3 d' },
  { hours: 168, label: '7 d' },
] as const

// One request in flight at a time, the same rule `fetchCompare` follows: a
// slow answer for an old point must not land after a fast one for a new point
// and overwrite it.
let inFlight: AbortController | null = null

export async function fetchSkill(lat: number, lon: number, hours: number): Promise<SkillBody> {
  inFlight?.abort()
  const controller = new AbortController()
  inFlight = controller

  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    hours: String(hours),
  })

  try {
    const resp = await fetch(`/api/skill?${params}`, { signal: controller.signal })
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`
      try {
        const body = await resp.json()
        if (body && typeof body.detail === 'string') detail = body.detail
      } catch {
        // Not JSON (a proxy error page). The status will do.
      }
      throw new Error(detail)
    }
    const parsed = await resp.json()
    // A shape check rather than a cast, for the reason `fetchCompare` states:
    // a proxy, a stale service worker or a dev server misrouting `/api` all
    // answer 200 with *something*, and a component that trusts the cast
    // crashes somewhere that looks like a bug in the panel.
    if (!parsed || !Array.isArray(parsed.scores) || !Array.isArray(parsed.buckets)) {
      throw new Error('/api/skill returned something that is not a skill report')
    }
    return parsed as SkillBody
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/** Test seam: the module-level controller outlives a component. */
export function _resetSkillInFlight(): void {
  inFlight = null
}
